import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Datatable} from '../../../components/Datatable/Datatable'
import {Button} from '../../../components/Button/Button'
import {ModalWrapper} from '../../../components/Modals/General/ModalWrapper/ModalWrapper'
import {Input} from '../../../components/Input/Input'
import useModals from '../../../hooks/useModals'
import {DELETE, GET, POST, PUT} from '../../../services/api'
import {Loading} from '../../../components/Loading/Loading'
import {ModalDelete} from '../../../components/Modals/General/ModalDelete/ModalDelete'
import Swal from 'sweetalert2'
import {i18n} from '../../../translate/i18n'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'

type PriceGroupRange = {
  minPeople: number
  maxPeople: number
  price: number
}

type BookingEnvironmentType = {
  name: string
  tables: number
  seatsAvailable: number
  pricePerSeat?: number | null
  pricePerEnvironment?: number | null
  pricePerTable?: number | null
  seatsPerTable?: number | null
  priceByGroup?: PriceGroupRange[] | null
  enableCapacityValidation?: boolean | null
}

const BookingEnvironmentContent: FC = () => {
  const {showWrapperModal, showDeleteModal} = useModals()

  const [environments, setEnvironments] = useState<BookingEnvironmentType[]>([])
  const [action, setAction] = useState<'creating' | 'editing' | 'viewing'>('creating')
  const [id, setId] = useState<number | null>(null)
  const [name, setName] = useState<string>('')
  const [tables, setTables] = useState(0)
  const [seatsAvailable, setSeatsAvailable] = useState(0)
  const [pricePerSeat, setPricePerSeat] = useState<number | null>(null)
  const [pricePerEnvironment, setPricePerEnvironment] = useState<number | null>(null)
  const [pricePerTable, setPricePerTable] = useState<number | null>(null)
  const [seatsPerTable, setSeatsPerTable] = useState<number | null>(null)
  const [priceByGroup, setPriceByGroup] = useState<PriceGroupRange[]>([])
  const [enableCapacityValidation, setEnableCapacityValidation] = useState(true)
  const [loading, setLoading] = useState(false)

  const getEnvironments = async () => {
    try {
      setLoading(true)
      const bookingEnvironmentRes = await GET('/queueReservation/bookingEnvironment')
      if (bookingEnvironmentRes.success && bookingEnvironmentRes.data) {
        // Backend salva em centavos, Datatable tipo 'money' espera centavos
        const normalizedData = bookingEnvironmentRes.data.map((env: any) => ({
          ...env,
          pricePerSeat: env.pricePerSeat ?? 0,
          pricePerEnvironment: env.pricePerEnvironment ?? 0,
          pricePerTable: env.pricePerTable ?? 0,
          seatsPerTable: env.seatsPerTable ?? null,
          priceByGroup: env.priceByGroup ?? null,
          priceByGroupFormatted: env.priceByGroup && env.priceByGroup.length > 0
            ? env.priceByGroup
                .map((range: any) => {
                  const priceInReais = (range.price / 100).toFixed(2).replace('.', ',')
                  return `${range.minPeople}-${range.maxPeople}: R$ ${priceInReais}`
                })
                .join(' | ')
            : '-',
        }))
        setEnvironments(normalizedData)
      } else {
        setEnvironments([])
      }
      setLoading(false)
    } catch (e) {
      setLoading(false)
      console.log(e)
    }
  }

  const createEnvironment = async () => {
    try {
      if (!name || !tables || !seatsAvailable) {
        Swal.fire({
          icon: 'error',
          title: 'Campos faltantes',
          text: 'Verifique se preencheu todos os campos obrigatórios!',
        })
      } else {
        // Validação: apenas um tipo de preço pode ser informado
        const pricesSet = [
          pricePerSeat, 
          pricePerEnvironment, 
          pricePerTable, 
          priceByGroup.length > 0 ? 1 : null
        ].filter((price) => price !== null && price > 0)
        
        if (pricesSet.length > 1) {
          Swal.fire({
            icon: 'warning',
            title: 'Atenção',
            text: 'Informe apenas um tipo de valor: por lugar, por ambiente, por mesa ou por grupo!',
          })
          return
        }

        // Validação específica para priceByGroup
        if (priceByGroup.length > 0) {
          console.log('🔍 [CREATE] Validando priceByGroup:', JSON.stringify(priceByGroup, null, 2))
          
          // Normaliza valores para number (Input type='number' pode retornar strings)
          const normalizedRanges = priceByGroup.map(range => ({
            minPeople: Number(range.minPeople),
            maxPeople: Number(range.maxPeople),
            price: Number(range.price)
          }))
          
          console.log('🔄 [CREATE] Após normalização:', JSON.stringify(normalizedRanges, null, 2))
          
          // Verifica cada faixa individualmente com logs
          for (let i = 0; i < normalizedRanges.length; i++) {
            const range = normalizedRanges[i]
            console.log(`📊 [CREATE] Faixa ${i + 1}:`, {
              minPeople: range.minPeople,
              maxPeople: range.maxPeople,
              price: range.price,
              'typeof minPeople': typeof range.minPeople,
              'typeof maxPeople': typeof range.maxPeople,
              'typeof price': typeof range.price,
              'price === null': range.price === null,
              'price === undefined': range.price === undefined,
              'price <= 0': range.price <= 0,
              'minPeople >= maxPeople': range.minPeople >= range.maxPeople
            })
          }
          
          // Verifica se todos os campos estão preenchidos e válidos
          const hasInvalidRange = normalizedRanges.some(
            range => 
              !range.minPeople || 
              !range.maxPeople || 
              range.price === null || 
              range.price === undefined || 
              range.price <= 0 || 
              isNaN(range.minPeople) ||
              isNaN(range.maxPeople) ||
              isNaN(range.price) ||
              range.minPeople >= range.maxPeople
          )
          
          if (hasInvalidRange) {
            console.error('❌ [CREATE] Validação FALHOU!')
            Swal.fire({
              icon: 'warning',
              title: 'Atenção',
              text: 'Todas as faixas devem ter valores válidos (maiores que zero) e mínimo menor que máximo! Pressione F12 para ver detalhes no console.',
            })
            return
          }
          
          console.log('✅ [CREATE] Validação passou!')

          // Atualiza priceByGroup com valores normalizados
          priceByGroup.splice(0, priceByGroup.length, ...normalizedRanges)

          // Verifica sobreposição de faixas
          for (let i = 0; i < priceByGroup.length; i++) {
            for (let j = i + 1; j < priceByGroup.length; j++) {
              const range1 = priceByGroup[i]
              const range2 = priceByGroup[j]
              if (
                (range1.minPeople <= range2.maxPeople && range1.maxPeople >= range2.minPeople) ||
                (range2.minPeople <= range1.maxPeople && range2.maxPeople >= range1.minPeople)
              ) {
                Swal.fire({
                  icon: 'warning',
                  title: 'Atenção',
                  text: 'As faixas de pessoas não podem se sobrepor!',
                })
                return
              }
            }
          }
        }

        setLoading(true)
        
        // Converte priceByGroup de reais para centavos
        const priceByGroupConverted = priceByGroup.length > 0 
          ? priceByGroup.map(range => ({
              minPeople: range.minPeople,
              maxPeople: range.maxPeople,
              price: Math.round(range.price * 100) // converte para centavos
            }))
          : null

        const environmentRes = await POST('/queueReservation/bookingEnvironment', {
          name,
          tables,
          seatsAvailable,
          // IntlCurrencyInput retorna em reais, converte para centavos antes de salvar
          pricePerSeat: pricePerSeat && pricePerSeat > 0 ? Math.round(pricePerSeat * 100) : null,
          pricePerEnvironment: pricePerEnvironment && pricePerEnvironment > 0 ? Math.round(pricePerEnvironment * 100) : null,
          pricePerTable: pricePerTable && pricePerTable > 0 ? Math.round(pricePerTable * 100) : null,
          seatsPerTable: seatsPerTable && seatsPerTable > 0 ? seatsPerTable : null,
          priceByGroup: priceByGroupConverted,
          enableCapacityValidation,
        })
        if (environmentRes.success === true) {
          Swal.fire({
            icon: 'success',
            title: 'Ambiente cadastrado',
            text: 'Cadastro realizado com sucesso!',
          }).then(async () => await getEnvironments())
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Erro',
            text: 'Erro ao cadastrar ambiente!',
          }).then(async () => await getEnvironments())
        }
        setLoading(false)
      }
    } catch (e) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao cadastrar ambiente!',
      }).then(async () => await getEnvironments())
      console.log(e)
    }
  }

  const editEnvironment = async () => {
    try {
      if (!name || !tables || !seatsAvailable) {
        Swal.fire({
          icon: 'error',
          title: 'Campos faltantes',
          text: 'Verifique se preencheu todos os campos obrigatórios!',
        })
      } else {
        // Validação: apenas um tipo de preço pode ser informado
        const pricesSet = [
          pricePerSeat, 
          pricePerEnvironment, 
          pricePerTable, 
          priceByGroup.length > 0 ? 1 : null
        ].filter((price) => price !== null && price > 0)
        
        if (pricesSet.length > 1) {
          Swal.fire({
            icon: 'warning',
            title: 'Atenção',
            text: 'Informe apenas um tipo de valor: por lugar, por ambiente, por mesa ou por grupo!',
          })
          return
        }

        // Validação específica para priceByGroup
        if (priceByGroup.length > 0) {
          console.log('🔍 [EDIT] Validando priceByGroup:', JSON.stringify(priceByGroup, null, 2))
          
          // Normaliza valores para number (Input type='number' pode retornar strings)
          const normalizedRanges = priceByGroup.map(range => ({
            minPeople: Number(range.minPeople),
            maxPeople: Number(range.maxPeople),
            price: Number(range.price)
          }))
          
          console.log('🔄 [EDIT] Após normalização:', JSON.stringify(normalizedRanges, null, 2))
          
          // Verifica cada faixa individualmente com logs
          for (let i = 0; i < normalizedRanges.length; i++) {
            const range = normalizedRanges[i]
            console.log(`📊 [EDIT] Faixa ${i + 1}:`, {
              minPeople: range.minPeople,
              maxPeople: range.maxPeople,
              price: range.price,
              'typeof minPeople': typeof range.minPeople,
              'typeof maxPeople': typeof range.maxPeople,
              'typeof price': typeof range.price,
              'price === null': range.price === null,
              'price === undefined': range.price === undefined,
              'price <= 0': range.price <= 0,
              'minPeople >= maxPeople': range.minPeople >= range.maxPeople
            })
          }
          
          // Verifica se todos os campos estão preenchidos e válidos
          const hasInvalidRange = normalizedRanges.some(
            range => 
              !range.minPeople || 
              !range.maxPeople || 
              range.price === null || 
              range.price === undefined || 
              range.price <= 0 || 
              isNaN(range.minPeople) ||
              isNaN(range.maxPeople) ||
              isNaN(range.price) ||
              range.minPeople >= range.maxPeople
          )
          
          if (hasInvalidRange) {
            console.error('❌ [EDIT] Validação FALHOU!')
            Swal.fire({
              icon: 'warning',
              title: 'Atenção',
              text: 'Todas as faixas devem ter valores válidos (maiores que zero) e mínimo menor que máximo! Pressione F12 para ver detalhes no console.',
            })
            return
          }
          
          console.log('✅ [EDIT] Validação passou!')

          // Atualiza priceByGroup com valores normalizados
          priceByGroup.splice(0, priceByGroup.length, ...normalizedRanges)

          // Verifica sobreposição de faixas
          for (let i = 0; i < priceByGroup.length; i++) {
            for (let j = i + 1; j < priceByGroup.length; j++) {
              const range1 = priceByGroup[i]
              const range2 = priceByGroup[j]
              if (
                (range1.minPeople <= range2.maxPeople && range1.maxPeople >= range2.minPeople) ||
                (range2.minPeople <= range1.maxPeople && range2.maxPeople >= range1.minPeople)
              ) {
                Swal.fire({
                  icon: 'warning',
                  title: 'Atenção',
                  text: 'As faixas de pessoas não podem se sobrepor!',
                })
                return
              }
            }
          }
        }

        setLoading(true)
        
        // Converte priceByGroup de reais para centavos
        const priceByGroupConverted = priceByGroup.length > 0 
          ? priceByGroup.map(range => ({
              minPeople: range.minPeople,
              maxPeople: range.maxPeople,
              price: Math.round(range.price * 100) // converte para centavos
            }))
          : null

        const environmentRes = await PUT(`/queueReservation/bookingEnvironment/${id}`, {
          name,
          tables,
          seatsAvailable,
          // IntlCurrencyInput retorna em reais, converte para centavos antes de salvar
          pricePerSeat: pricePerSeat && pricePerSeat > 0 ? Math.round(pricePerSeat * 100) : null,
          pricePerEnvironment: pricePerEnvironment && pricePerEnvironment > 0 ? Math.round(pricePerEnvironment * 100) : null,
          pricePerTable: pricePerTable && pricePerTable > 0 ? Math.round(pricePerTable * 100) : null,
          seatsPerTable: seatsPerTable && seatsPerTable > 0 ? seatsPerTable : null,
          priceByGroup: priceByGroupConverted,
          enableCapacityValidation,
        })
        if (environmentRes.success === true) {
          Swal.fire({
            icon: 'success',
            title: 'Ambiente alterado',
            text: 'Edição realizada com sucesso!',
          }).then(async () => await getEnvironments())
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Erro',
            text: 'Erro ao alterar ambiente!',
          }).then(async () => await getEnvironments())
        }
        setLoading(false)
      }
    } catch (e) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao alterar ambiente!',
      }).then(async () => await getEnvironments())
      console.log(e)
    }
  }

  const deleteEnvironment = async () => {
    try {
      setLoading(true)
      const environmentRes = await DELETE(`/queueReservation/bookingEnvironment/${id}`)
      if (environmentRes.success === true) {
        Swal.fire({
          icon: 'success',
          title: 'Ambiente deletado',
          text: 'Deleção realizada com sucesso!',
        }).then(async () => await getEnvironments())
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Erro ao deletar modalidade!',
        }).then(async () => await getEnvironments())
      }
      setLoading(false)
    } catch (e) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao deletar modalidade!',
      }).then(async () => await getEnvironments())
      console.log(e)
    }
  }

  useEffect(() => {
    getEnvironments()
  }, [])

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t(`environment.index`)}
                </span>
                <BreadCrumbs />
              </h3>
              <div className='card-toolbar'>
                <Button
                  text={i18n.t(`buttons.add_environment`)}
                  color='primary'
                  iconColor='#FFF'
                  icon='fas fa-plus'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={() => {
                    showWrapperModal(i18n.t(`buttons.add_environment`))
                    setName('')
                    setTables(0)
                    setSeatsAvailable(0)
                    setPricePerSeat(null)
                    setPricePerEnvironment(null)
                    setPricePerTable(null)
                    setPriceByGroup([])
                    setEnableCapacityValidation(true)
                    setId(null)
                    setAction('creating')
                  }}
                  btnClass='mb-5 fw-bolder'
                />
              </div>
            </div>
            <div className='card-body'>
              <Datatable
                data={environments}
                headers={[
                  {key: i18n.t(`environment.name`), value: 'name', type: 'default'},
                  {key: i18n.t(`environment.tables`), value: 'tables', type: 'default'},
                  {
                    key: i18n.t(`environment.seats_available`),
                    value: 'seatsAvailable',
                    type: 'default',
                  },
                  {
                    key: 'Valor por Lugar',
                    value: 'pricePerSeat',
                    type: 'money',
                  },
                  {
                    key: 'Valor por Ambiente',
                    value: 'pricePerEnvironment',
                    type: 'money',
                  },
                  {
                    key: 'Valor por Mesa',
                    value: 'pricePerTable',
                    type: 'money',
                  },
                  {
                    key: 'Faixas de Preço',
                    value: 'priceByGroupFormatted',
                    type: 'default',
                  },
                  {
                    key: 'Validação de Capacidade',
                    value: 'enableCapacityValidation',
                    type: 'default',
                  },
                  {key: i18n.t(`environment.actions`), value: 'acoes', type: ''},
                ]}
                options={[
                  {
                    icon: 'fas fa-eye',
                    action: (item) => {
                      showWrapperModal(
                        `${i18n.t(`general.view`)} ${i18n.t(`environment.environment`)}`
                      )
                      setAction('viewing')
                      setId(item.id)
                      setName(item.name)
                      setTables(item.tables)
                      setSeatsAvailable(item.seatsAvailable)
                      setPricePerSeat(item.pricePerSeat ? item.pricePerSeat / 100 : null)
                      setPricePerEnvironment(item.pricePerEnvironment ? item.pricePerEnvironment / 100 : null)
                      setPricePerTable(item.pricePerTable ? item.pricePerTable / 100 : null)
                      setSeatsPerTable(item.seatsPerTable ?? null)
                      setPriceByGroup(
                        item.priceByGroup && item.priceByGroup.length > 0
                          ? item.priceByGroup.map((range: PriceGroupRange) => ({
                              minPeople: range.minPeople,
                              maxPeople: range.maxPeople,
                              price: range.price / 100,
                            }))
                          : []
                      )
                      setEnableCapacityValidation(item.enableCapacityValidation ?? true)
                    },
                    title: `${i18n.t(`general.view`)} ${i18n.t(`environment.environment`)}`,
                  },
                  {
                    icon: 'fas fa-pen',
                    action: (item) => {
                      showWrapperModal(
                        `${i18n.t(`general.edit`)} ${i18n.t(`environment.environment`)}`
                      )
                      setAction('editing')
                      setId(item.id)
                      setName(item.name)
                      setTables(item.tables)
                      setSeatsAvailable(item.seatsAvailable)
                      // Backend retorna em centavos, mas Input money espera valor em reais
                      // Divide por 100 para o Input processar corretamente
                      setPricePerSeat(item.pricePerSeat ? item.pricePerSeat / 100 : null)
                      setPricePerEnvironment(item.pricePerEnvironment ? item.pricePerEnvironment / 100 : null)
                      setPricePerTable(item.pricePerTable ? item.pricePerTable / 100 : null)
                      setSeatsPerTable(item.seatsPerTable ?? null)
                      // Backend retorna priceByGroup em centavos, converte para reais
                      setPriceByGroup(
                        item.priceByGroup && item.priceByGroup.length > 0
                          ? item.priceByGroup.map((range: PriceGroupRange) => ({
                              minPeople: range.minPeople,
                              maxPeople: range.maxPeople,
                              price: range.price / 100, // converte para reais
                            }))
                          : []
                      )
                      setEnableCapacityValidation(item.enableCapacityValidation ?? true)
                    },
                    title: `${i18n.t(`general.edit`)} ${i18n.t(`environment.environment`)}`,
                  },
                  {
                    icon: 'fas fa-trash',
                    action: (item) => {
                      showDeleteModal(
                        `${i18n.t(`general.delete`)} ${i18n.t(`environment.environment`)}`
                      )
                      setId(item.id)
                    },
                    title: `${i18n.t(`general.delete`)} ${i18n.t(`environment.environment`)}`,
                  },
                ]}
                color='primary'
              />
            </div>
          </div>

          <ModalWrapper
            noAction={action === 'viewing'}
            confirmAction={
              action === 'creating' ? () => createEnvironment() : () => editEnvironment()
            }
          >
            <div className='d-flex flex-column gap-3'>
              {/* Campos obrigatórios - Grid responsivo 3 colunas */}
              <div className='row g-3'>
                <div className='col-md-6'>
                  <Input
                    inputAttr={{
                      type: 'default',
                    }}
                    value={name}
                    change={(value: string) => {
                      const isOnlyNumbers = /^\d+$/.test(value.trim())
                      if (!isOnlyNumbers || value.trim() === '') {
                        setName(value)
                      }
                    }}
                    placeholder={i18n.t(`environment.name`)}
                    label={i18n.t(`environment.name`)}
                    disabled={action === 'viewing'}
                  />
                </div>
                <div className='col-md-3'>
                  <Input
                    inputAttr={{
                      type: 'number',
                    }}
                    value={tables}
                    change={(value: number) => {
                      if (value >= 0) setTables(value)
                    }}
                    placeholder={i18n.t(`environment.tables`)}
                    label={i18n.t(`environment.tables`)}
                    disabled={action === 'viewing'}
                  />
                </div>
                <div className='col-md-3'>
                  <Input
                    inputAttr={{
                      type: 'number',
                    }}
                    value={seatsAvailable}
                    change={(value: number) => {
                      if (value >= 0) setSeatsAvailable(value)
                    }}
                    placeholder={i18n.t(`environment.seats_available`)}
                    label={i18n.t(`environment.seats_available`)}
                    disabled={action === 'viewing'}
                  />
                </div>
              </div>

              {/* Divisor e instruções */}
              <div className='mt-2'>
                <hr className='mb-2' />
                <div className='alert alert-info d-flex align-items-center py-2 px-3'>
                  <i className='fas fa-info-circle me-2'></i>
                  <small>
                    <strong>Valores de Cobrança (Opcional):</strong> Preencha apenas UM tipo de
                    valor. Ao preencher um campo, os demais serão desabilitados.
                  </small>
                </div>
              </div>

              {/* Campos de preço em grid compacto */}
              <div className='row g-3'>
                <div className='col-4'>
                  <Input
                    inputAttr={{
                      type: 'money',
                    }}
                    disabled={
                      action === 'viewing' ||
                      (pricePerEnvironment !== null && pricePerEnvironment > 0) ||
                      (pricePerTable !== null && pricePerTable > 0) ||
                      priceByGroup.length > 0
                    }
                    currency='BRL'
                    locale='pt-BR'
                    value={pricePerSeat ?? 0}
                    change={(value: number) => {
                      if (!value || value === 0) {
                        setPricePerSeat(null)
                      } else {
                        setPricePerSeat(value)
                      }
                    }}
                    placeholder='R$ 0,00'
                    label='Por Lugar (R$)'
                  />
                </div>
                <div className='col-4'>
                  <Input
                    inputAttr={{
                      type: 'money',
                    }}
                    disabled={
                      action === 'viewing' ||
                      (pricePerSeat !== null && pricePerSeat > 0) ||
                      (pricePerTable !== null && pricePerTable > 0) ||
                      priceByGroup.length > 0
                    }
                    currency='BRL'
                    locale='pt-BR'
                    value={pricePerEnvironment ?? 0}
                    change={(value: number) => {
                      if (!value || value === 0) {
                        setPricePerEnvironment(null)
                      } else {
                        setPricePerEnvironment(value)
                      }
                    }}
                    placeholder='R$ 0,00'
                    label='Por Ambiente (R$)'
                  />
                </div>
                <div className='col-4'>
                  <Input
                    inputAttr={{
                      type: 'money',
                    }}
                    disabled={
                      action === 'viewing' ||
                      (pricePerSeat !== null && pricePerSeat > 0) ||
                      (pricePerEnvironment !== null && pricePerEnvironment > 0) ||
                      priceByGroup.length > 0
                    }
                    currency='BRL'
                    locale='pt-BR'
                    value={pricePerTable ?? 0}
                    change={(value: number) => {
                      if (!value || value === 0) {
                        setPricePerTable(null)
                      } else {
                        setPricePerTable(value)
                      }
                    }}
                    placeholder='R$ 0,00'
                    label='Por Mesa (R$)'
                  />
                </div>
              </div>

              {/* Campo Lugares por Mesa - usado quando pricePerTable está definido */}
              {(pricePerTable !== null && pricePerTable > 0) && (
                <div className='row mb-3'>
                  <div className='col-4'>
                    <Input
                      inputAttr={{
                        type: 'number',
                        min: 1,
                        max: 20,
                      }}
                      disabled={action === 'viewing'}
                      value={seatsPerTable ?? ''}
                      change={(value: number) => {
                        if (!value || value === 0) {
                          setSeatsPerTable(null)
                        } else {
                          setSeatsPerTable(Math.round(value))
                        }
                      }}
                      placeholder='Ex: 4'
                      label='Lugares por Mesa (média)'
                      required
                    />
                    <small className='text-muted'>
                      Quantas pessoas cabem por mesa em média. Usado para calcular automaticamente o número de mesas necessárias.
                    </small>
                  </div>
                </div>
              )}

              {/* Seção de Preço por Grupo */}
              <div className='mt-3'>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <label className='form-label fw-bold mb-0'>
                    <i className='fas fa-users me-2'></i>
                    Valor por Grupo de Pessoas
                  </label>
                  <Button
                    text='Adicionar Faixa'
                    color='success'
                    iconColor='#FFF'
                    icon='fas fa-plus'
                    size='small'
                    horizontalPadding={4}
                    disabled={
                      action === 'viewing' ||
                      (pricePerSeat !== null && pricePerSeat > 0) ||
                      (pricePerEnvironment !== null && pricePerEnvironment > 0) ||
                      (pricePerTable !== null && pricePerTable > 0)
                    }
                    btnAttr={{
                      type: 'button',
                    }}
                    click={() => {
                      setPriceByGroup([
                        ...priceByGroup,
                        { minPeople: 1, maxPeople: 1, price: 0 },
                      ])
                    }}
                  />
                </div>

                {priceByGroup.length > 0 && (
                  <div className='border rounded p-3 bg-light'>
                    {priceByGroup.map((range, index) => (
                      <div key={index} className='card mb-2'>
                        <div className='card-body p-3'>
                          <div className='d-flex justify-content-between align-items-center mb-2'>
                            <h6 className='mb-0'>Faixa {index + 1}</h6>
                            {action !== 'viewing' && (
                              <button
                                type='button'
                                className='btn btn-sm btn-danger'
                                onClick={() => {
                                  const newRanges = priceByGroup.filter((_, i) => i !== index)
                                  setPriceByGroup(newRanges)
                                }}
                              >
                                <i className='fas fa-trash'></i>
                              </button>
                            )}
                          </div>
                          <div className='row g-2'>
                            <div className='col-4'>
                              <Input
                                inputAttr={{
                                  type: 'number',
                                  min: '1',
                                }}
                                value={range.minPeople}
                                change={(value: number) => {
                                  const newRanges = [...priceByGroup]
                                  newRanges[index].minPeople = value > 0 ? value : 1
                                  setPriceByGroup(newRanges)
                                }}
                                placeholder='1'
                                label='Mín. Pessoas'
                                disabled={action === 'viewing'}
                              />
                            </div>
                            <div className='col-4'>
                              <Input
                                inputAttr={{
                                  type: 'number',
                                  min: '1',
                                }}
                                value={range.maxPeople}
                                change={(value: number) => {
                                  const newRanges = [...priceByGroup]
                                  newRanges[index].maxPeople = value > 0 ? value : 1
                                  setPriceByGroup(newRanges)
                                }}
                                placeholder='999'
                                label='Máx. Pessoas'
                                disabled={action === 'viewing'}
                              />
                            </div>
                            <div className='col-4'>
                              <Input
                                inputAttr={{
                                  type: 'money',
                                }}
                                currency='BRL'
                                locale='pt-BR'
                                value={range.price}
                                change={(value: number) => {
                                  const newRanges = [...priceByGroup]
                                  newRanges[index].price = value
                                  setPriceByGroup(newRanges)
                                }}
                                placeholder='R$ 0,00'
                                label='Valor (R$)'
                                disabled={action === 'viewing'}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Checkbox para Validação de Capacidade */}
              <div className='row g-3 mt-2'>
                <div className='col-12'>
                  <hr className='mb-2' />
                  <div className='d-flex align-items-center'>
                    <input
                      type='checkbox'
                      id='enableCapacityValidation'
                      checked={enableCapacityValidation}
                      onChange={(e) => setEnableCapacityValidation(e.target.checked)}
                      disabled={action === 'viewing'}
                      className='form-check-input'
                      style={{cursor: 'pointer'}}
                    />
                    <label
                      htmlFor='enableCapacityValidation'
                      className='form-check-label ms-2'
                      style={{cursor: 'pointer'}}
                    >
                      <strong>Validar capacidade do ambiente</strong>
                      <small className='d-block text-muted'>
                        Ao ativar, o sistema impedirá reservas que excedam a capacidade total (mesas e lugares disponíveis) para um período específico.
                      </small>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </ModalWrapper>

          <ModalDelete confirmAction={() => deleteEnvironment()} />
        </div>
      )}
    </>
  )
}

const BookingEnvironment: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: true},
          {title: i18n.t(`environment.map`), path: 'booking/environment', isActive: true},
        ]}
      >
        {i18n.t(`environment.map`)}
      </PageTitle>
      <BookingEnvironmentContent />
    </>
  )
}

export {BookingEnvironment}
